/**
 * Mock Interceptor — routes requests to local mock server when enabled.
 *
 * HOW IT WORKS:
 * 1. Checks if VITE_USE_MOCK=true in .env.local
 * 2. Checks if the request path is registered in API_REGISTRY with enabled: true
 * 3. If both conditions are met, redirects the request to mock server
 *
 * REQUIREMENTS:
 * - Set VITE_USE_MOCK=true in .env.local
 * - Add endpoint to mockApiRegistry.ts with enabled: true
 * - Run mock server: npm run mock
 */

import type {AxiosInstance, InternalAxiosRequestConfig} from 'axios'
import {shouldMockPath} from '../mock/mockApiRegistry'

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true'
const MOCK_BASE_URL = 'http://localhost:3001'

/**
 * Checks if the request path should be mocked
 */
const isPathEligibleForMock = (config: InternalAxiosRequestConfig): boolean => {
  const url = config.url || ''
  const method = (config.method || 'get').toLowerCase()
  const urlPathOnly = url.split('?')[0]
  return shouldMockPath(urlPathOnly, method)
}

/**
 * Attaches the mock interceptor to an Axios instance.
 * Runs FIRST (before auth, cache, throttle) to redirect mock requests.
 */
export const attachMockInterceptor = (axiosInstance: AxiosInstance): AxiosInstance => {
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (USE_MOCK && isPathEligibleForMock(config)) {
        // Redirect to mock server
        config.baseURL = MOCK_BASE_URL
        // Prepend /api to match mock server's route structure
        if (config.url && !config.url.startsWith('/api')) {
          config.url = `/api${config.url}`
        }

        // Remove auth header - mock server doesn't need it
        delete config.headers['Authorization']

        // Mark request as mocked (visible in Network tab)
        config.headers['X-Mocked'] = 'true'

        if (import.meta.env.DEV) {
          console.log(`[Mock] ${config.method?.toUpperCase()} ${config.url}`)
        }
      }

      return config
    },
    error => Promise.reject(error),
  )

  return axiosInstance
}
